import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tp * { box-sizing:border-box; }
  .tp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tpFade .4s ease both; }
  @keyframes tpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tp-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tp-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-orange { background:#f97316; color:white; } .btn-orange:hover { box-shadow:0 4px 14px rgba(249,115,22,.3); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:10px 14px; font-size:.76rem; color:#c2410c; margin-bottom:1.4rem; display:flex; align-items:center; gap:8px; }

  .info-box { background:#eef2ff; border:1px solid #c7d2fe; border-radius:12px; padding:12px 16px; margin-bottom:1.4rem; font-size:.78rem; color:#3730a3; line-height:1.6; }
  .info-box strong { font-weight:700; }

  .types-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:1rem; margin-bottom:1.4rem; }
  .type-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; display:flex; align-items:center; justify-content:space-between; gap:10px; transition:all .15s; }
  .type-card:hover { border-color:#f97316; box-shadow:0 4px 14px rgba(249,115,22,.08); }
  .type-card.inactive { opacity:.5; }
  .type-info { display:flex; align-items:center; gap:10px; }
  .type-order { width:26px; height:26px; border-radius:7px; background:#fff7ed; color:#f97316; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
  .type-name  { font-size:.86rem; font-weight:600; color:#0f1123; }
  .type-status{ font-size:.67rem; color:#9094a8; margin-top:2px; }
  .type-actions { display:flex; gap:6px; flex-shrink:0; }

  .tp-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .tp-loading { display:flex; align-items:center; justify-content:center; height:150px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:440px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus { border-color:#f97316; background:white; }

  .modal-step { margin-bottom:18px; }
  .modal-step:last-of-type { margin-bottom:0; }
  .modal-step-head { display:flex; align-items:center; gap:8px; margin-bottom:10px; }
  .modal-step-num { width:20px; height:20px; border-radius:50%; background:#f97316; color:white; font-size:.68rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-step-title { font-size:.82rem; font-weight:700; color:#0f1123; }
  .modal-step-note { font-size:.72rem; color:#6b7194; margin:-4px 0 12px; line-height:1.5; }
  .modal-step-divider { border:none; border-top:1px dashed #e0e4f0; margin:0 0 18px; }

  @media (max-width:768px) {
    .tp-title { font-size:1.1rem; }
    .tp-sub   { font-size:.72rem; }
    .tp-header { margin-bottom:1rem; }
    .types-grid { grid-template-columns:1fr; }
    .info-box { font-size:.74rem; }
    .modal { padding:18px; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }

  @media (max-width:400px) {
    .type-actions .btn-sm { padding:5px 7px; }
  }
`;

const CoordTermPeriods = () => {
  const [terms,   setTerms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | { mode:'add'|'edit'|'toggle', term? }
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ name:'', order:'0' });
  const [note,    setNote]    = useState('');

  useEffect(() => { fetchTerms(); }, []);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/coordinator/term-periods');
      setTerms(data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openAdd  = () => { setForm({ name:'', order: String(terms.length) }); setNote(''); setModal({ mode:'add' }); };
  const openEdit = (t) => {
    setForm({ name: t.name, order: String(t.order) });
    setNote('');
    setModal({ mode:'edit', term: t });
  };
  const openToggle = (t) => { setNote(''); setModal({ mode:'toggle', term: t }); };

  const submit = async () => {
    if (!note.trim()) { toast.error('Reason is required'); return; }
    const { mode, term } = modal;

    let payload;
    if (mode === 'add' || mode === 'edit') {
      if (!form.name.trim()) { toast.error('Name is required'); return; }
      payload = {
        action: mode,
        name: form.name.trim(),
        order: Number(form.order) || 0,
        note,
      };
      if (mode === 'edit') payload.termId = term._id;
    } else if (mode === 'toggle') {
      payload = { action: 'toggle', termId: term._id, name: term.name, active: !term.active, note };
    }

    try {
      setSaving(true);
      await api.post('/coordinator/requests', { type: 'term_period_action', payload });
      toast.success('Request submitted — awaiting admin approval');
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getModalTitle = () => {
    if (!modal) return '';
    if (modal.mode === 'add')    return 'Request: Add Term';
    if (modal.mode === 'edit')   return `Request: Edit "${modal.term?.name}"`;
    if (modal.mode === 'toggle') return `Request: ${modal.term?.active ? 'Deactivate' : 'Activate'} "${modal.term?.name}"`;
    return '';
  };

  return (
    <div className="tp">
      <style>{style}</style>

      <div className="tp-header">
        <div>
          <div className="tp-title">Terms</div>
          <div className="tp-sub">View term periods used in Term-Based grading · submit requests to add, edit or deactivate</div>
        </div>
        <button className="btn btn-orange" onClick={openAdd}><PlusIcon />Add Term</button>
      </div>

      <div className="req-banner">
        📋 All changes (add, edit, toggle active) require admin approval.
      </div>

      <div className="info-box">
        <strong>How this works:</strong> Each term you add becomes a period teachers can grade against in Term-Based mode.
        Every term is worth 100% for each subject. Use <strong>Order</strong> to control the sequence terms appear in.
        Inactive terms won't appear in the teacher dropdown.
      </div>

      {loading ? (
        <div className="tp-loading"><div className="spinner"/>Loading…</div>
      ) : terms.length === 0 ? (
        <div className="tp-empty">No terms yet. Submit a request to add your first term — e.g. "1st Bi-Monthly".</div>
      ) : (
        <div className="types-grid">
          {terms.map(t => (
            <div key={t._id} className={`type-card ${!t.active ? 'inactive' : ''}`}>
              <div className="type-info">
                <div className="type-order">{t.order}</div>
                <div>
                  <div className="type-name">{t.name}</div>
                  <div className="type-status">{t.active ? 'Active' : 'Inactive'}</div>
                </div>
              </div>
              <div className="type-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => openToggle(t)} title={t.active ? 'Request deactivate' : 'Request activate'}>
                  {t.active ? '●' : '○'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><PencilSquareIcon /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && createPortal(
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{getModalTitle()}</div>
              <button className="modal-close" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>

            {modal.mode === 'toggle' && (
              <div style={{textAlign:'center',padding:'1rem 0 .5rem'}}>
                <div style={{fontSize:'2rem',marginBottom:12}}>{modal.term?.active ? '🔴' : '🟢'}</div>
                <div style={{fontWeight:700,fontSize:'.92rem',color:'#0f1123',marginBottom:6}}>
                  {modal.term?.active ? 'Deactivate' : 'Activate'} "{modal.term?.name}"?
                </div>
                <div style={{fontSize:'.78rem',color:'#9094a8',marginBottom:14}}>
                  {modal.term?.active
                    ? 'Inactive terms won\'t appear in the teacher dropdown.'
                    : 'Active terms appear in the teacher dropdown when grading.'}
                </div>
              </div>
            )}

            {(modal.mode === 'add' || modal.mode === 'edit') && (
              <>
                <div className="modal-step">
                  <div className="modal-step-head">
                    <div className="modal-step-num">1</div>
                    <div className="modal-step-title">Term</div>
                  </div>
                  <div className="modal-step-note">Every term is worth 100% for each subject.</div>
                  <div className="form-field">
                    <label className="form-label">Name *</label>
                    <input type="text" className="form-input" value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      placeholder="e.g. 1st Bi-Monthly, Mid-Term, Final Term…" autoFocus />
                  </div>
                  <div className="form-field" style={{marginBottom:0}}>
                    <label className="form-label">Order (sequence position)</label>
                    <input type="number" className="form-input" value={form.order} min="0"
                      onChange={e => setForm({...form, order: e.target.value})} />
                  </div>
                </div>

                <hr className="modal-step-divider" />
              </>
            )}

            <div className="form-field" style={{marginBottom:4}}>
              <label className="form-label">Note for admin</label>
              <input type="text" className="form-input" required value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Reason for this request…" />
            </div>

            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#c2410c',marginTop:8}}>
              📋 This request will be sent to admin for approval.
            </div>

            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submit} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>

      , document.body)}
    </div>
  );
};

export default CoordTermPeriods;
